
import React, { useState, useRef, useCallback, useEffect } from "react";
import * as Material from "@mui/material";
import { Text, Input, Table, StatusChip, DatePicker } from "@fragments";
import {
    IconAdjustments,
    IconArrowBackUp,
    IconCircleFilled,
    IconDotsVertical,
    IconUsers,
    IconCalendar,
    IconCurrencyDollar,
    IconQrcode,
} from "@tabler/icons-react";

import { ReactComponent as MastercardIcon } from '@assets/custom-icons/mastercard.svg';
import { ReactComponent as EloIcon } from '@assets/custom-icons/elo.svg';
import { ReactComponent as VisaIcon } from '@assets/custom-icons/visa.svg';

import api from "@helpers/api.js";
import Parser from "@helpers/parser.js";
import { useCommunity } from "contexts/community";

import Popper from "@fragments/Popper";
import { UserAvatar } from "@components/Community";

import SearchBar from "../../components/SearchBar";
import MetricCard from "../../components/MetricCard";
import Filter from "../../components/Filter";

import "./styles.scss";

function pad2(n) {
    return String(n).padStart(2, '0');
}

function toApiDate(d) {
    if (!d) return null;
    const date = d instanceof Date ? d : new Date(d);
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function toDisplayDate(d) {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}

export default function ChargesSection() {
    const sharedPopperRef = useRef();
    const rowActionsPopperRef = useRef();
    const intervalPickerRef = useRef();
    const { communities } = useCommunity();

    // Estados compartilhados entre os componentes
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedCommunity, setSelectedCommunity] = useState('all');
    const [searchMode, setSearchMode] = useState('email');

    const [dateRange, setDateRange] = useState({
        presetId: null,
        from: null,
        to: null
    });

    const searchQueryRef = useRef('');

    // Estados de dados e loading centralizados
    const [charges, setCharges] = useState([]);
    const [summary, setSummary] = useState({
        paid: { records: 0, amount: 0 },
        refunded: { records: 0, amount: 0 },
        pending: { records: 0, amount: 0 }
    });
    const [loading, setLoading] = useState(true);

    // Estados de paginação
    const [pagination, setPagination] = useState({
        pages: 0,
        records: 0,
        page: 1
    });

    const rowsSize = 15;

    const fetchData = useCallback(async (searchValue = searchQueryRef.current, pageNumber = pagination.page) => {
        try {
            setLoading(true);

            const params = new URLSearchParams();

            if (selectedStatus !== 'all') {
                params.append('filter[status]', selectedStatus);
            }

            if (selectedCommunity !== 'all') {
                params.append('community', selectedCommunity);
            }

            // Adicionar filtros de data
            if (dateRange.from && dateRange.to) {
                params.append('filter[date_from]', toApiDate(dateRange.from));
                params.append('filter[date_to]', toApiDate(dateRange.to));
            }

            if (searchValue.trim()) {
                if (searchMode === 'email') {
                    params.append('filter[email]', searchValue.trim());
                } else {
                    params.append('filter[name]', searchValue.trim());
                }
            }

            // Adicionar parâmetros de paginação
            params.append('page', pageNumber.toString());
            params.append('per_page', rowsSize);

            const { data } = await api.get(`charge/search?${params.toString()}`);

            setCharges(data.data || []);
            setSummary(data.summary || {
                paid: { records: 0, amount: 0 },
                refunded: { records: 0, amount: 0 },
                pending: { records: 0, amount: 0 }
            });

            // Atualizar dados de paginação
            setPagination({
                pages: data.pages || 0,
                records: data.records || 0,
                page: data.page || 1
            });

        } catch (error) {
            console.error('Error fetching charges:', error);
            setCharges([]);
            setSummary({
                paid: { records: 0, amount: 0 },
                refunded: { records: 0, amount: 0 },
                pending: { records: 0, amount: 0 }
            });
            setPagination({
                pages: 0,
                records: 0,
                page: 1
            });
        } finally {
            setLoading(false);
        }
    }, [selectedStatus, selectedCommunity, dateRange, searchMode, pagination.page]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchData(searchQueryRef.current, 1);
    }, [selectedStatus, selectedCommunity, dateRange, searchMode]);

    const executeSearch = useCallback(() => {
        fetchData(searchQueryRef.current);
    }, [fetchData]);

    const handlePageChange = useCallback((newPage) => {
        fetchData(searchQueryRef.current, newPage);
    }, [fetchData]);

    function FiltersRow() {
        const statusOptions = [
            { id: 'all', label: 'Todos' },
            { id: 'paid', label: 'Paga' },
            { id: 'refunded', label: 'Reembolsada' },
            { id: 'pending', label: 'Pendente' },
        ];

        const communityOptions = [
            { id: 'all', label: 'Todas' },
            ...(communities || []).filter(comm => comm?.isAdmin()).map(comm => ({
                id: comm.id,
                label: comm.name
            }))
        ];


        function handleStatusFilterClick(event) {
            const options = statusOptions.map(option => ({
                ...option,
                onClick: () => handleStatusSelect(option.id)
            }));

            sharedPopperRef.current.open(options, 'bottom', event.currentTarget);
        }

        function handleCommunityFilterClick(event) {
            const options = communityOptions.map(option => ({
                ...option,
                onClick: () => handleCommunitySelect(option.id)
            }));

            sharedPopperRef.current.open(options, 'bottom', event.currentTarget);
        }

        function handleStatusSelect(status) {
            setSelectedStatus(status);
            sharedPopperRef.current.close();
        }

        function handleCommunitySelect(communityId) {
            setSelectedCommunity(communityId);
            sharedPopperRef.current.close();
        }

        function handleDateFilterClick(event) {
            intervalPickerRef.current?.open(event.currentTarget, {
                presetId: dateRange.presetId,
                from: dateRange.from,
                to: dateRange.to,
            });
        }

        function handleDateApply({ presetId, from, to }) {
            setDateRange({ presetId, from, to });
        }

        function handleDateClear() {
            setDateRange({ presetId: null, from: null, to: null });
        }

        const hasDateRange = !!(dateRange.from && dateRange.to);
        const dateLabel = hasDateRange
            ? `${toDisplayDate(dateRange.from)} — ${toDisplayDate(dateRange.to)}`
            : 'Todas as datas';


        return (
            <>
                <Text.h4 value="Filtros" className="mt-3" left={<IconAdjustments />} />

                <div className="filters-row mt-1">

                    <Filter
                        name="Status"
                        valueLabel={statusOptions.find(s => s.id === selectedStatus)?.label || 'Todos'}
                        onClick={handleStatusFilterClick}
                        icon={<IconCircleFilled size={16} />}
                    />
                    <Filter
                        name="Comunidade"
                        valueLabel={communityOptions.find(c => c.id === selectedCommunity)?.label || 'Todas as comunidades'}
                        onClick={handleCommunityFilterClick}
                        icon={<IconUsers size={16} />}
                    />
                    <Filter
                        name="Data"
                        valueLabel={dateLabel}
                        onClick={handleDateFilterClick}
                        onClear={hasDateRange ? handleDateClear : undefined}
                        icon={<IconCalendar size={16} />}
                    />

                </div>

                <DatePicker.Interval
                    controlRef={intervalPickerRef}
                    value={dateRange}
                    maxDate={new Date()}
                    onApply={handleDateApply}
                />

                <Popper controlRef={sharedPopperRef}>
                    <div style={{ display: 'none' }} />
                </Popper>
            </>
        )
    }



    function ChargesTableSection() {

        const getMethodIcon = (method, cardBrand) => {
            if (method === 'credit') {
                const brand = cardBrand?.toLowerCase() || 'visa';
                return (
                    <>
                        {{
                            'visa': <VisaIcon />,
                            'elo': <EloIcon />,
                            'mastercard': <MastercardIcon />
                        }[brand]}
                    </>
                );
            } else if (method === 'pix') {
                return <IconQrcode />;
            }
            return <IconCurrencyDollar />;
        };

        const formatAmount = (amount, installments, currency) => {
            if (!amount) return Parser.price.format(0, currency);

            if (installments && installments > 1) {
                const installmentAmount = amount / installments;
                return `${Parser.price.format(amount, currency)} (${installments}x ${Parser.price.format(installmentAmount, currency)})`;
            }

            return Parser.price.format(amount, currency);
        };

        const getCardNumber = (charge) => {
            if (charge.method === 'credit' && charge.card?.last_four) {
                return `• ${charge.card.last_four}`;
            }
            return '';
        };

        const handleUserClick = (e, userId) => {
            e.stopPropagation();
            if (e.ctrlKey || e.metaKey) {
                // Abrir em nova aba
                window.open(`/user/${userId}`, '_blank');
            } else {
                // Navegar normalmente
                window.navigate(`/user/${userId}`);
            }
        };

        const columns = [
            {
                id: 'client',
                title: 'Cliente',
                grow: 1,
                className: 'pl-1',
                customRender: (data) => (
                    <div className="client-info">
                        <UserAvatar user={data.user} size={28} />
                        <Text.span contrast={7} weight={400}>
                            {data.user?.username || 'N/A'}
                        </Text.span>
                    </div>
                )
            },
            {
                id: 'status',
                title: 'Status',
                align: 'center',
                minWidth: 120,
                customRender: (data) => {
                    // Only the positive status gets a colored pill
                    const StatusComponent = data.status === 'paid' ? StatusChip.Success : StatusChip.Default;

                    return <StatusComponent text={Parser.payment.chargeStatus(data.status)} />;
                }
            },
            {
                id: 'amount',
                title: 'Valor',
                minWidth: 150,
                render: (data) => formatAmount(data.amount, data.installments, data.currency)
            },
            {
                id: 'method',
                title: 'Método',
                align: 'center',
                minWidth: 110,
                className: 'payment-method-cell',
                customRender: (data) => (
                    <>
                        {getMethodIcon(data.method, data.card?.brand)}
                        {data.method === 'credit' && (
                            <label className="card-number">
                                {getCardNumber(data)}
                            </label>
                        )}
                    </>
                )
            },
            {
                id: 'date',
                title: 'Data',
                minWidth: 120,
                // Payment date when paid; creation date for pending/canceled charges
                render: (data) => Parser.date.readable(data.timestamps?.paid || data.timestamps?.created)
            }
        ];

        const handleOpenActions = (event, charge) => {
            event.stopPropagation();

            const options = [{
                id: 'refund',
                label: 'Realizar estorno',
                icon: <IconArrowBackUp size={16} />,
                variant: 'warning',
                onClick: () => {
                    window.openConfirm({
                        title: 'Realizar estorno',
                        subtitle: 'Tem certeza que deseja realizar o estorno desta cobrança?',
                        buttons: [
                            {
                                text: "Sim, realizar estorno",
                                onClick: async () => {
                                    try {
                                        await api.delete(`charge/${charge.id}/refund`);
                                        fetchData();
                                    } catch (error) {
                                        console.error('Erro ao realizar estorno:', error);
                                    }
                                },
                                variant: "contained",
                                color: "error",
                            },
                            {
                                text: "Manter cobrança",
                                variant: "text",
                            },
                        ],
                    });
                }
            }];

            rowActionsPopperRef.current.open(options, 'bottom', event.currentTarget);
        };

        return (
            <div className="charges-table mt-3 mb-6">
                <Table
                    id="banking-charges-table"
                    data={charges}
                    loading={loading}
                    loadingRows={4}
                    emptyMessage="Nenhuma cobrança encontrada"
                    columns={columns}
                    enablePagination={true}
                    pages={pagination.pages}
                    records={pagination.records}
                    page={pagination.page}
                    onSelectPage={handlePageChange}
                    onRowClick={(item, e) => handleUserClick(e, item.user?.id)}
                    contained
                    actionsComponent={(charge) => (
                        <button
                            className={`action-button ${charge.status?.toLowerCase() !== 'paid' ? 'disabled' : ''}`}
                            onClick={(event) => handleOpenActions(event, charge)}
                            disabled={charge.status?.toLowerCase() !== 'paid'}
                        >
                            <IconDotsVertical size={20} />
                        </button>
                    )}
                />
                <Popper controlRef={rowActionsPopperRef} />
            </div>
        )
    }

    // One formatted total per currency (e.g. "R$ 150,00 · $ 12.00") so USD
    // never gets conflated into the BRL figure.
    const formatSummaryAmounts = (bucket) => {
        const entries = Object.entries(bucket?.currencies || {});
        if (!entries.length) return Parser.price.format(bucket?.amount || 0, 'BRL');

        return entries
            .map(([currency, values]) => Parser.price.format(values.amount, currency))
            .join(' · ');
    };

    return (
        <div>
            <SearchBar
                searchMode={searchMode}
                setSearchMode={setSearchMode}
                searchQueryRef={searchQueryRef}
                onSearch={executeSearch}
            />

            <FiltersRow />

            <div className="metrics-row mt-3">
                <MetricCard
                    title="Pagas"
                    metric={summary.paid.records}
                    submetric={formatSummaryAmounts(summary.paid)}
                    loading={loading}
                />
                <MetricCard
                    title="Reembolsadas"
                    metric={summary.refunded.records}
                    submetric={formatSummaryAmounts(summary.refunded)}
                    loading={loading}
                />
                <MetricCard
                    title="Aguardando pagamento"
                    metric={summary.pending.records}
                    submetric={formatSummaryAmounts(summary.pending)}
                    loading={loading}
                />
            </div>

            <ChargesTableSection />
        </div>
    )
}
